import type { ReactElement } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { errorMessage } from '../lib/api'
import { useT } from '../lib/i18n'
import { Button, ButtonLink } from '../components/ui/Button'
import { ErrorState } from '../components/ui/ErrorState'
import { Skeleton } from '../components/ui/Skeleton'

/** Shown while a lazily-loaded screen's chunk is in flight. Shaped like the page it precedes. */
export const RouteFallback = (): ReactElement => (
  <div className="flex flex-col gap-6" aria-busy="true">
    <div className="flex flex-col gap-2">
      <Skeleton width="14rem" height={28} />
      <Skeleton width="22rem" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} shape="block" height={116} />
      ))}
    </div>
    <Skeleton shape="block" height={280} />
  </div>
)

/**
 * The router's error element. Covers a failed chunk load, a thrown loader error and a
 * genuine 404 — each with its own sentence, none of them apologising.
 */
export const RouteErrorBoundary = (): ReactElement => {
  const t = useT()
  const error = useRouteError()

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="page-shell py-16">
        <ErrorState
          title={t('shell.routeError.notFoundTitle')}
          description={t('shell.routeError.notFoundBody')}
          onRetry={undefined}
        />
        <div className="flex justify-center">
          <ButtonLink to="/app/dashboard" variant="primary">
            {t('shell.routeError.backToDashboard')}
          </ButtonLink>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell py-16">
      <ErrorState
        title={t('shell.routeError.failedTitle')}
        description={errorMessage(error)}
        onRetry={() => {
          window.location.reload()
        }}
        retryLabel={t('shell.routeError.reload')}
      />
    </div>
  )
}

/** Standalone 404 for addresses the router matched to nothing. */
export const NotFound = (): ReactElement => {
  const t = useT()
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-paper px-4 text-center">
      {/* An HTTP status code, not copy. It reads the same in every language. */}
      <p className="t-mono text-ink-mute">404</p>
      <h1 className="t-h1 text-ink">{t('shell.notFound.title')}</h1>
      <p className="t-body max-w-md text-ink-mute">{t('shell.notFound.body')}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <ButtonLink to="/" variant="secondary">
          {t('shell.notFound.home')}
        </ButtonLink>
        <ButtonLink to="/app/dashboard" variant="primary">
          {t('shell.notFound.dashboard')}
        </ButtonLink>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          window.history.back()
        }}
      >
        {t('common.actions.back')}
      </Button>
    </div>
  )
}
